// CustomersPage.jsx
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  getClientes,
  crearCliente,
  actualizarCliente,
  cambiarEstadoCliente,
} from "../../../services/admin/clientes";
import CustomerFormModal from "./CustomerFormModal";
import CustomerAddressesModal from "./CustomerAddressesPage";
import { IconEdit, IconLocation } from "../../../icons/Icons";
import { hasPerm } from "../../../utils/hasPerm";
import "./CustomersPage.css";

const normalizePhoneCode = (v) => {
  const raw = String(v ?? "").trim();
  if (!raw) return "";
  if (raw.startsWith("+")) return raw;
  if (/^\d+$/.test(raw)) return `+${raw}`;
  return raw;
};

const formatFullPhone = (code, phone) => {
  const c = normalizePhoneCode(code);
  const p = String(phone ?? "").trim();
  if (c && p) return `${c} ${p}`;
  if (p) return p;
  return c || "";
};

function CustomersPage() {
  const [clientes, setClientes] = useState([]);
  // > 0 cuando el servidor devolvio solo una parte del listado.
  const [listaRecortada, setListaRecortada] = useState(0);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 12;
  const [showModal, setShowModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [selectedAddressCustomer, setSelectedAddressCustomer] = useState(null);
  const [alert, setAlert] = useState(null);
  const alertTimerRef = useRef(null);

  /* Los dados de baja no salen por defecto: estorban en el listado de
     todos los dias. La casilla es la unica forma de volver a encontrarlos
     para reactivarlos. */
  const [verInactivos, setVerInactivos] = useState(false);
  const [cambiandoEstado, setCambiandoEstado] = useState(null);

  const showAlert = useCallback((type, message) => {
    setAlert({ type, message: String(message || "") });

    if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    alertTimerRef.current = window.setTimeout(() => setAlert(null), 3500);
  }, []);

  useEffect(() => {
    return () => {
      if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    };
  }, []);

  const cargarClientes = useCallback(async () => {
    try {
      const { items, total, hasMore } = await getClientes({
        incluirInactivos: verInactivos,
      });
      setClientes(items);
      setListaRecortada(hasMore ? total : 0);
    } catch (error) {
      console.error("Error cargando clientes:", error);
      setListaRecortada(0);
      showAlert("error", error?.message || "Failed to load customers.");
      setClientes([]);
    }
  }, [showAlert, verInactivos]);

  useEffect(() => {
    cargarClientes();
  }, [cargarClientes]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clientes;

    return clientes.filter((c) => {
      const blob = [
        c?.cliente_id,
        c?.nombre,
        c?.apellido,
        c?.correo,
        c?.telefono,
        c?.codigo_telefono,
        c?.codigo_pais,
        c?.country_code,
        c?.phone_country_code,
        c?.empresa,
        c?.razon_social,
        c?.vat_tax_id,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return blob.includes(q);
    });
  }, [clientes, search]);

  const totalPages = useMemo(() => {
    return Math.ceil(filtered.length / pageSize) || 1;
  }, [filtered.length, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
    if (page < 1) setPage(1);
  }, [page, totalPages]);

  const start = (page - 1) * pageSize;
  const paginated = filtered.slice(start, start + pageSize);

  const nextPage = () => page < totalPages && setPage((p) => p + 1);
  const prevPage = () => page > 1 && setPage((p) => p - 1);

  const openCreateModal = () => {
    setSelectedCustomer(null);
    setShowModal(true);
  };

  const openEditModal = (customer) => {
    setSelectedCustomer(customer);
    setShowModal(true);
  };

  /* Se pregunta antes: dar de baja le saca el acceso al store a alguien
     que puede estar en medio de una compra. No borra nada, y el texto lo
     dice, porque "deactivate" a secas suena a que se pierde el historial. */
  const alternarEstado = async (cliente) => {
    const activo = cliente.activo !== false;

    const confirmado = window.confirm(
      activo
        ? `Deactivate ${cliente.nombre} ${cliente.apellido}?\n\n` +
            "They will no longer be able to sign in to the store. " +
            "Their orders, addresses and invoices are kept."
        : `Reactivate ${cliente.nombre} ${cliente.apellido}?`
    );

    if (!confirmado) return;

    setCambiandoEstado(cliente.cliente_id);

    try {
      const res = await cambiarEstadoCliente(cliente.cliente_id, !activo);
      showAlert("success", res?.message || "Customer updated.");
      await cargarClientes();
    } catch (error) {
      console.error("Error cambiando estado del cliente:", error);
      showAlert(
        "error",
        error?.response?.data?.message || "Could not change the customer status."
      );
    } finally {
      setCambiandoEstado(null);
    }
  };

  const handleSubmit = async (form) => {
    try {
      if (selectedCustomer) {
        const res = await actualizarCliente(selectedCustomer.cliente_id, form);
        showAlert("success", res?.message || "Customer updated successfully.");
      } else {
        const res = await crearCliente(form);
        showAlert("success", res?.message || "Customer created successfully.");
      }

      await cargarClientes();
      setShowModal(false);
    } catch (error) {
      console.error("Error guardando cliente:", error);
      throw error;
    }
  };

  return (
    <div className="customers-page">
      {listaRecortada > 0 && (
        <div className="lista-recortada">
          Showing the first {clientes.length} of {listaRecortada} records. Refine your
          filters or raise the page size to see the rest.
        </div>
      )}

      {alert && (
        <div className={`omx-alert omx-alert--${alert.type}`}>
          <span className="omx-alert__msg">{alert.message}</span>
          <button className="omx-alert__x" onClick={() => setAlert(null)}>
            ✕
          </button>
        </div>
      )}

      <div className="customers-page__header">
        <h2 className="customers-page__title">Customers Management</h2>

        <div className="customers-page__topbar">
          <input
            type="text"
            placeholder="Search by name, email, phone code..."
            className="customers-page__search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />

          <label className="customers-page__verBajas">
            <input
              type="checkbox"
              checked={verInactivos}
              onChange={(e) => {
                setVerInactivos(e.target.checked);
                setPage(1);
              }}
            />
            Show deactivated
          </label>

          {hasPerm("Create customer") && (
            <button className="customers-page__create" onClick={openCreateModal}>
              Create Customer
            </button>
          )}
        </div>
      </div>

      <div className="customers-page__tableCard">
        <table className="customers-page__table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Lastname</th>
              <th>Account</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Created</th>
              <th className="customers-page__thRight">Actions</th>
            </tr>
          </thead>

          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan="8" className="customers-page__noData">
                  No customers found
                </td>
              </tr>
            ) : (
              paginated.map((c) => {
                const phoneCode =
                  c?.codigo_telefono ??
                  c?.codigo_pais ??
                  c?.country_code ??
                  c?.phone_country_code ??
                  "";

                const esEmpresa = c?.tipo_cliente === "comercial";
                const activo = c?.activo !== false;
                const nombreEmpresa = c?.razon_social || c?.empresa || "";

                return (
                  <tr
                    key={c.cliente_id}
                    className={activo ? "" : "customers-page__rowOff"}
                  >
                    <td>{c.cliente_id}</td>
                    <td>{c.nombre}</td>
                    <td>{c.apellido}</td>

                    {/* El tipo y, si es empresa, a nombre de quien se
                        factura. Sin esto no habia forma de distinguir un
                        taller de un particular en el listado. */}
                    <td>
                      <span
                        className={`customers-page__tag ${
                          esEmpresa
                            ? "customers-page__tag--company"
                            : "customers-page__tag--person"
                        }`}
                      >
                        {esEmpresa ? "Company" : "Individual"}
                      </span>

                      {esEmpresa && nombreEmpresa ? (
                        <div className="customers-page__empresa">
                          {nombreEmpresa}
                          {c?.vat_tax_id ? ` · ${c.vat_tax_id}` : ""}
                        </div>
                      ) : null}

                      {!activo ? (
                        <div className="customers-page__baja">Deactivated</div>
                      ) : null}
                    </td>

                    <td>{formatFullPhone(phoneCode, c.telefono) || "-"}</td>

                    <td>{c.correo}</td>
                    <td>{c.fecha_creado?.slice(0, 10)}</td>

                    <td className="customers-page__actionsCell">
                      <div className="customers-page__actions">
                        {hasPerm("Update customer") && (
                          <span
                            className="customers-page__actionIcon customers-page__actionIcon--edit"
                            onClick={() => openEditModal(c)}
                            title="Edit customer"
                          >
                            {IconEdit}
                          </span>
                        )}

                        {hasPerm("Update direccion customer") && (
                          <span
                            className="customers-page__actionIcon customers-page__actionIcon--view"
                            onClick={() => {
                              setSelectedAddressCustomer(c);
                              setShowAddressModal(true);
                            }}
                            title="View addresses"
                          >
                            {IconLocation}
                          </span>
                        )}

                        {hasPerm("Update customer") && (
                          <button
                            type="button"
                            className={`customers-page__estadoBtn ${
                              activo ? "" : "customers-page__estadoBtn--on"
                            }`}
                            onClick={() => alternarEstado(c)}
                            disabled={cambiandoEstado === c.cliente_id}
                            title={
                              activo
                                ? "Deactivate customer"
                                : "Reactivate customer"
                            }
                          >
                            {cambiandoEstado === c.cliente_id
                              ? "…"
                              : activo
                              ? "Deactivate"
                              : "Reactivate"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="customers-page__pagination">
        <button
          className="customers-page__pageBtn"
          onClick={prevPage}
          disabled={page === 1}
        >
          Prev
        </button>

        <span className="customers-page__pageInfo">
          Page {page} of {totalPages}
        </span>

        <button
          className="customers-page__pageBtn"
          onClick={nextPage}
          disabled={page === totalPages}
        >
          Next
        </button>
      </div>

      {showModal && (
        <CustomerFormModal
          isOpen={showModal}
          customerData={selectedCustomer}
          onClose={() => setShowModal(false)}
          onSubmit={handleSubmit}
        />
      )}

      {showAddressModal && (
        <CustomerAddressesModal
          isOpen={showAddressModal}
          cliente={selectedAddressCustomer}
          onClose={() => setShowAddressModal(false)}
        />
      )}
    </div>
  );
}

export default CustomersPage;
